<script>
  import { onMount } from "svelte";
  import * as d3 from "d3";

  export let data = [];
  export let type; // String, what type of sparkline it is (state or district)
  export let update_date; // String, when the last update happened from guff

  const margin = { top: 5, right: 18, bottom: 20, left: 15 };
  let width = 90;
  let height = 40;

  let svgEl;
  let tooltipEl;

  function updateWidth() {
    let minInnerWidth = window.innerWidth < 500 ? 75 : 90;
    width = Math.min(minInnerWidth, window.innerWidth - 2);
    height = 40;
  }

  // --- Function to turn string of last updated date and determine the start of that month ---
  function getStartMonthLatestUpdate(update_date) {
    const updateDate = new Date(update_date);
    return d3.utcMonth.floor(updateDate);
  }

 // --- Function to determine the last complete month of data, if it's not complete, it goes to the last month ---
function findMonthtoStart(currentMonthStart, latest_data_date) {
  // Find the last day of the month of the data update date
  const lastDayOfUpdateMonth = d3.utcDay.offset(d3.utcMonth.ceil(latest_data_date), -1);

  // If the latest_data_date is the last day of the month, consider it complete
  const isLastDayOfMonth = latest_data_date.getTime() === lastDayOfUpdateMonth.getTime();
  
  const lastCompleteMonth = isLastDayOfMonth
    ? currentMonthStart  // Complete month - use current month
    : d3.utcMonth.offset(currentMonthStart, -1);  // Incomplete - go back one month
    
  return lastCompleteMonth;
}


  // --- Utility to fill missing months with 0 ---
function groupByMonthsWithZeros(rows) {
  if (!rows.length) return [];

  const minDate = d3.utcMonth.floor(
    d3.min(data, d => d3.utcMonth.floor(new Date(d.date_filed)))
  );

  const currentMonthStart = getStartMonthLatestUpdate(update_date)
  const lastCompleteMonth = findMonthtoStart(currentMonthStart, new Date(update_date))

  const allMonths = d3.utcMonths(minDate, d3.utcMonth.offset(lastCompleteMonth, 1));

  const rollup = d3.rollup(
    rows,
    v => v.length,
    d => d3.utcMonth.floor(new Date(d.date_filed))
  );

  const monthlyData = allMonths.map(month => ({
    date: month,
    count: rollup.get(month) || 0
  }));

  return monthlyData;
}

  let x, y, xAxisGroup, yAxisGroup, linePath;

  function initChart() {
    if (!data.length) return;

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();

    const points = groupByMonthsWithZeros(data);

    // -- Find if the last updated date for the WHOLE dataset - not just this state/district has been a complete month -- //
    // -- This is because we want the last month shown to be the same for all states/districts -- //

    const currentMonthStart =  getStartMonthLatestUpdate(update_date)
    const lastCompleteMonth = findMonthtoStart(currentMonthStart, new Date(update_date))

    // --- Scales ---
    x = d3.scaleUtc()
      .domain([new Date("2025-01-01"), lastCompleteMonth])
      .range([margin.left, width - margin.right]);

    y = d3.scaleLinear()
      .domain([0, d3.max(points, d => d.count)])
      .nice()
      .range([height - margin.bottom, margin.top]);
      
       // --- X axis ---
    const xAxis = d3.axisBottom(x).tickValues([new Date("2025-01-01"),lastCompleteMonth]).tickFormat(
     type == "district" ? "" : d3.utcFormat("%b '%y"));
    xAxisGroup = svg.append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(xAxis)
      .call(g => {
        g.select(".domain").attr("stroke", "none")
        g.selectAll(".tick line").attr("stroke", "var(--sparkline-text-colors)").attr("stroke-width", 1);
        g.selectAll(".tick text").style("font-size", "10px").style("fill", "var(--sparkline-text-colors)");
      });


    // --- Line ---
    const line = d3.line()
      .x(d => x(d.date))
      .y(d => y(d.count))
      .curve(d3.curveMonotoneX);

    linePath = svg.append("path")
      .datum(points)
      .attr("fill", "none")
      .attr("stroke", "var(--sparkline-line-colors)")
      .attr("stroke-width", 2)
      .attr("d", line);
  }

  function updateChart() {
    if (!data.length) return;

    const points = groupByMonthsWithZeros(data);

    const yNew = d3.scaleLinear()
      .domain([0, d3.max(points, d => d.count)])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const yAxis = d3.axisLeft(yNew).ticks(3);
   
    const line = d3.line()
      .x(d => x(d.date))
      .y(d => yNew(d.count))
      .curve(d3.curveMonotoneX);

    
  }

  onMount(() => {
    updateWidth();
    initChart();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  });

  $: svgEl && data.length && updateChart();
</script>

<style>

</style>

<svg class={'sparkline'} bind:this={svgEl} {width} {height}></svg>
